import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Spinner, Alert, ProgressBar } from 'react-bootstrap';
import { 
  FaExclamationTriangle, FaCheckCircle, FaClipboardCheck,
  FaTshirt, FaGraduationCap, FaClipboardList, FaUsers,
  FaArrowRight, FaChartLine, FaFileAlt, FaUserCheck
} from 'react-icons/fa';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// Normalizar API_URL (remover barra final si existe)
const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4004').replace(/\/$/, '');

// Helper para obtener headers de auth
const getAuthHeaders = () => {
  const storedUser = localStorage.getItem('user');
  const user = storedUser ? JSON.parse(storedUser) : null;
  const token = localStorage.getItem('token');
  
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-user-id': String(user?.id || user?.Legajo || user?.CdEmpleado || '1'),
    'x-user-role': String(user?.IdRol || user?.tipoUsuario || '1')
  };
};

const AdminDashboardCards = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados para cada sección
  const [reclamosStats, setReclamosStats] = useState({ pendientes: 0, resueltos: 0, validados: 0 });
  const [tallesStats, setTallesStats] = useState({ cargados: 0, pendientes: 0 });
  const [capacitacionesCount, setCapacitacionesCount] = useState(0);
  const [colaboradoresCount, setColaboradoresCount] = useState(0);
    const fetchCapacitacionesCount = async () => {
      try {
        const res = await fetch(`${API_URL}/api/capacitaciones/categorias`, {
          headers: getAuthHeaders()
        });
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) {
          return data.data.length;
        }
        return 0;
      } catch (err) {
        console.error('Error fetching capacitaciones:', err);
        return 0;
      }
    };

    const fetchColaboradoresCount = async () => {
      try {
        const res = await fetch(`${API_URL}/api/empleados`, {
          headers: getAuthHeaders()
        });
        const data = await res.json();
        if (Array.isArray(data)) {
          return data.length;
        }
        return 0;
      } catch (err) {
        console.error('Error fetching colaboradores:', err);
        return 0;
      }
    };
  
  const RECLAMOS_COLORS = ['#FFC107', '#28A745', '#17A2B8'];

  const fetchReclamosStats = async () => {
    try {
      const token = localStorage.getItem('token');
      
      console.log('📊 Fetching reclamos stats from:', `${API_URL}/api/reclamos`);
      
      // Obtener todos los reclamos
      const response = await axios.get(`${API_URL}/api/reclamos`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('✅ Reclamos response:', response.data);
      
      // El endpoint retorna un objeto con success y data
      const reclamos = response.data.data || response.data;
      
      // Calcular estadísticas según las columnas Protesta, Resuelto, Valida
      // Pendientes: tienen Protesta=1 pero NO están resueltos (Resuelto=0)
      const pendientes = reclamos.filter(r => r.Protesta === 1 && r.Resuelto === 0).length;
      
      // Resueltos: tienen Resuelto=1
      const resueltos = reclamos.filter(r => r.Resuelto === 1).length;
      
      // Validados: tienen Valida=1 (sin protesta)
      const validados = reclamos.filter(r => r.Valida === 1 && r.Protesta === 0).length;
      
      console.log('📈 Stats calculadas:', { pendientes, resueltos, validados, total: reclamos.length });
      
      return { pendientes, resueltos, validados };
    } catch (err) {
      console.error('❌ Error fetching reclamos stats:', err);
      return { pendientes: 0, resueltos: 0, validados: 0 };
    }
  };

  const fetchTallesStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/indumentaria/talles`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        // Solo contar empleados con al menos un talle cargado
        const cargados = data.data.filter(t => t.camisa_invierno || t.pantalon_invierno || t.campera || t.camisa_verano || t.remera || t.pantalon_verano || t.borcego).length;
        return { cargados, pendientes: 0 };
      }
      return { cargados: 0, pendientes: 0 };
    } catch (err) {
      console.error('Error fetching talles:', err);
      return { cargados: 0, pendientes: 0 };
    }
  };

  useEffect(() => {
    const cargarTodo = async () => {
      try {
        setLoading(true);
        
        const [reclamosData, talles, capacitaciones, colaboradores] = await Promise.all([
          fetchReclamosStats(),
          fetchTallesStats(),
          fetchCapacitacionesCount(),
          fetchColaboradoresCount()
        ]);

        setReclamosStats(reclamosData);
        setTallesStats(talles);
        setCapacitacionesCount(capacitaciones);
        setColaboradoresCount(colaboradores);
      } catch (err) {
        setError(err.message || 'Error al cargar las estadísticas');
      } finally {
        setLoading(false);
      }
    };

    cargarTodo();
  }, []);

  const reclamosChartData = [
    { name: 'Pendientes', value: reclamosStats.pendientes },
    { name: 'Resueltos', value: reclamosStats.resueltos },
    { name: 'Validados', value: reclamosStats.validados },
  ];

  const totalReclamos = reclamosStats.pendientes + reclamosStats.resueltos + reclamosStats.validados;
  const porcentajeResueltos = totalReclamos > 0 ? Math.round(((reclamosStats.resueltos + reclamosStats.validados) / totalReclamos) * 100) : 0;

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Cargando dashboard...</p>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger" className="mt-4">{error}</Alert>;
  }

  return (
    <div className="admin-dashboard">
      {/* Accesos Rápidos */}
      <Row className="mb-4">
        <Col xs={12}>
          <h5 className="text-muted mb-3">
            <FaChartLine className="me-2" />
            Accesos Rápidos
          </h5>
        </Col>
        {[ 
          { icon: <FaClipboardList size={20} />, title: 'Reclamos', path: '/administrador/reclamos', color: '#FFC107', count: reclamosStats.pendientes, label: 'pendientes' },
          { icon: <FaTshirt size={20} />, title: 'Talles', path: '/administrador/talles', color: '#17A2B8', count: tallesStats.cargados, label: 'registros' },
          { icon: <FaGraduationCap size={20} />, title: 'Capacitaciones', path: '/administrador/resumen-capacitaciones', color: '#28A745', count: capacitacionesCount, label: 'resumen' },
          { icon: <FaUsers size={20} />, title: 'Colaboradores', path: '/administrador/administrar_usuarios', color: '#6F42C1', count: colaboradoresCount, label: 'gestión' },
        ].map((item, idx) => (
          <Col key={idx} lg={3} md={6} className="mb-3">
            <Card 
              className="h-100 shadow-sm quick-access-card" 
              style={{ cursor: 'pointer', borderLeft: `4px solid ${item.color}` }}
              onClick={() => navigate(item.path)}
            >
              <Card.Body className="d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="text-muted mb-1">{item.title}</h6>
                  <h3 className="mb-0" style={{ color: item.color }}>{item.count}</h3>
                  <small className="text-muted">{item.label}</small>
                </div>
                <div style={{ color: item.color, opacity: 0.3 }}>
                  {React.cloneElement(item.icon, { size: 40 })}
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Sección Reclamos */}
      <Row className="mb-4">
        <Col lg={8}>
          <Card className="shadow-sm h-100">
            <Card.Body>
              <Row>
                {[
                  { key: 'pendientes', icon: <FaExclamationTriangle />, color: 'warning', label: 'Pendientes' },
                  { key: 'resueltos', icon: <FaCheckCircle />, color: 'success', label: 'Resueltos' },
                  { key: 'validados', icon: <FaClipboardCheck />, color: 'info', label: 'Validados' },
                ].map((item, idx) => (
                  <Col key={idx} md={4} className="text-center mb-3 mb-md-0">
                    <div className={`text-${item.color} mb-2`}>{React.cloneElement(item.icon, { size: 28 })}</div>
                    <h2 className="mb-0">{reclamosStats[item.key]}</h2>
                    <small className="text-muted text-uppercase">{item.label}</small>
                  </Col>
                ))}
              </Row>
              <hr />
              <div className="mt-3">
                <div className="d-flex justify-content-between mb-2">
                  <span>Tasa de resolución</span>
                  <strong>{porcentajeResueltos}%</strong>
                </div>
                <ProgressBar>
                  <ProgressBar variant="success" now={(reclamosStats.resueltos / Math.max(totalReclamos, 1)) * 100} key={1} />
                  <ProgressBar variant="info" now={(reclamosStats.validados / Math.max(totalReclamos, 1)) * 100} key={2} />
                </ProgressBar>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4}>
          <Card className="shadow-sm h-100">
            <Card.Body>
              <h6 className="text-center mb-3">Distribución</h6>
              {reclamosChartData.some(item => item.value > 0) ? (
                <div style={{ height: '180px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={reclamosChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {reclamosChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={RECLAMOS_COLORS[index % RECLAMOS_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`${value}`, 'Cantidad']} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-center text-muted py-4">Sin datos</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Sección Talles */}
      <Row>
        <Col lg={6}>
          <Card className="shadow-sm">
            <Card.Header className="bg-info bg-opacity-10">
              <h6 className="mb-0">
                <FaTshirt className="me-2 text-info" />
                Registro de Talles
              </h6>
            </Card.Header>
            <Card.Body className="text-center">
              <div className="d-flex justify-content-center align-items-center">
                <div className="me-4">
                  <FaUsers size={48} className="text-info opacity-50" />
                </div>
                <div>
                  <h2 className="mb-0 text-info">{tallesStats.cargados}</h2>
                  <span className="text-muted">Colaboradores con talles registrados</span>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={6}>
          <Card className="shadow-sm h-100">
            <Card.Header className="bg-secondary bg-opacity-10">
              <h6 className="mb-0">
                <FaChartLine className="me-2 text-secondary" />
                Resumen General
              </h6>
            </Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                <span>Total Reclamos</span>
                <strong>{totalReclamos}</strong>
              </div>
              <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                <span>Pendientes</span>
                <strong className="text-warning">{reclamosStats.pendientes}</strong>
              </div>
              <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                <span>Resueltos</span>
                <strong className="text-success">{reclamosStats.resueltos}</strong>
              </div>
              <div className="d-flex justify-content-between align-items-center py-2">
                <span>Talles Cargados</span>
                <strong>{tallesStats.cargados}</strong>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AdminDashboardCards;